import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Loader2, Trophy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weekly Success Coaching questions, shown like the Enrollment Summary questions
const SuccessCoachingQuestionsDialog = ({ open, caseId, weekInfo, onComplete, onSkip }) => {
  const questions = weekInfo?.week?.questions || [];
  const required = Math.min(weekInfo?.required_answers || 3, questions.length);
  const [answers, setAnswers] = useState({});
  const [winWall, setWinWall] = useState('');
  const [discussion, setDiscussion] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setAnswers({}); setWinWall(''); setDiscussion(''); }
  }, [open]);

  const answeredCount = questions.filter(q => (answers[q] || '').trim()).length;

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/case-notes/generate-success-coaching-summary`, {
        case_id: caseId,
        answers: questions.map(q => ({ question: q, answer: answers[q] || '' })),
        win_wall: winWall,
        general_discussion: discussion
      });
      onComplete(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate the Personalized Encounter Summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onSkip()}>
      <DialogContent className="sm:max-w-2xl" data-testid="success-coaching-questions-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            Success Coaching — Week {weekInfo?.week_number}
          </DialogTitle>
          <DialogDescription>
            {weekInfo?.topic ? <span className="font-medium text-foreground">{weekInfo.topic}</span> : 'No topic set for this week.'}
            {' '}Answer at least {required} question{required === 1 ? '' : 's'}; the rest are optional.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Badge variant={answeredCount >= required ? 'default' : 'outline'} data-testid="sc-answered-count">
            {answeredCount} of {required} required answered
          </Badge>
          {questions.map((q, i) => (
            <div key={i} className="space-y-1">
              <Label className="text-sm">{i + 1}. {q}</Label>
              <Textarea rows={2} value={answers[q] || ''} onChange={(e) => setAnswers(prev => ({ ...prev, [q]: e.target.value }))}
                data-testid={`sc-answer-${i}`} />
            </div>
          ))}
          <div className="space-y-1 rounded-md bg-amber-50 border border-amber-200 p-3">
            <Label className="text-sm flex items-center gap-1"><Trophy className="w-4 h-4 text-amber-600" />Win-Wall: {weekInfo?.win_wall_question}</Label>
            <Textarea rows={2} value={winWall} onChange={(e) => setWinWall(e.target.value)} className="bg-white" data-testid="sc-win-wall-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">General discussion (anything else to include)</Label>
            <Textarea rows={3} value={discussion} onChange={(e) => setDiscussion(e.target.value)} data-testid="sc-discussion-input" />
          </div>
        </DialogBody>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onSkip} disabled={loading} data-testid="sc-skip-btn">Skip for Now</Button>
          <Button onClick={submit} disabled={loading || answeredCount < required} data-testid="sc-generate-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Generate Summary
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuccessCoachingQuestionsDialog;
